const _ = require("lodash");
const Math2D = require("./Math2D");
const CanvasUtil = require("./CanvasUtil");
const {FLAGS} = require("./GlobalFlags");
const defaultColors = require("./ColorUtil").getCurrentScheme();

const getShadowZoomCorrection = function(context) {
	const realDPR = CanvasUtil.getDPR(context);
	return Math.min(realDPR, 1);
};

// Creates a clipping path
const createRoundedClippingRegion = function(ctx, x, y, w, h, radius) {
	const cornerRadius = radius || Math.min(w, h) / 10;
	ctx.beginPath();
	ctx.moveTo(x + cornerRadius, y);
	ctx.lineTo(x + w - cornerRadius, y);
	ctx.quadraticCurveTo(x + w, y, x + w, y + cornerRadius);
	ctx.lineTo(x + w, y + h - cornerRadius);
	ctx.quadraticCurveTo(x + w, y + h, x + w - cornerRadius, y + h);
	ctx.lineTo(x + radius, y + h);
	ctx.quadraticCurveTo(x, y + h, x, y + h - cornerRadius);
	ctx.lineTo(x, y + cornerRadius);
	ctx.quadraticCurveTo(x, y, x + radius, y);
	ctx.closePath();
	ctx.clip();
};

//draws a rectangle with rounded corners, cornerRadius is optional (defaults to 10)
const drawRoundedRect = function(context, x, y, w, h, cornerRadius) {
	cornerRadius = cornerRadius || Math.min(w, h) / 10;
	context.beginPath();
	context.moveTo(x + cornerRadius, y);
	context.arcTo(x + w, y, x + w, y + cornerRadius, cornerRadius);
	context.arcTo(x + w, y + h, x + w - cornerRadius, y + h, cornerRadius);
	context.arcTo(x, y + h, x, y + h - cornerRadius, cornerRadius);
	context.arcTo(x, y, x + cornerRadius, y, cornerRadius);
	context.closePath();
};

// blurSize is a required prop.
const setContextOutsetShadow = function(ctx, props) {
	const zoomCorrectionMultiplier = getShadowZoomCorrection(ctx);
	const shadowColor = props.shadowColor || defaultColors.defaultShadow;
	const blurSize = props.blurSize;
	const blurOffset = props.blurOffset || blurSize / 8;
	ctx.setShadowBlur(blurSize / 2);
	ctx.shadowColor = shadowColor;
	ctx.shadowOffsetX = blurOffset * zoomCorrectionMultiplier;
	ctx.shadowOffsetY = blurOffset * zoomCorrectionMultiplier;
};

// blurSize is a required prop.
const drawOutsetRect = function(ctx, rect, props) {
	ctx.save();
	const fillColor = props.fillColor || defaultColors.defaultFill;
	setContextOutsetShadow(ctx, props);
	ctx
		.bp()
		.rect(rect)
		.fill(fillColor);
	ctx.restore();
};

// blurSize is a required prop.
const drawInsetRect = function(ctx, rect, props) {
	const zoomCorrectionMultiplier = getShadowZoomCorrection(ctx);
	ctx.save();
	const fillColor = props.fillColor;
	const hasThickerOutline = props.hasThickerOutline;
	const outlineColor = props.outlineColor || defaultColors.rectOutline;
	const shadowColor = props.shadowColor || defaultColors.defaultInsetShadow;
	const blurSize = props.blurSize;
	const lineWidth = CanvasUtil.getOptimalLineWidth(ctx);
	const adjRect = Math2D.padRect(rect, lineWidth / 2);
	ctx.bp().rect(adjRect);
	ctx.clip();
	whiteOutAfterClip(ctx, fillColor);
	ctx.setShadowBlur(blurSize);
	ctx.shadowColor = shadowColor;
	ctx.lineWidth = blurSize / 2;
	const blurOffset = ctx.lineWidth / 2;
	ctx.shadowOffsetX = blurOffset * zoomCorrectionMultiplier;
	ctx.shadowOffsetY = blurOffset * zoomCorrectionMultiplier;
	ctx
		.bp()
		.rect(Math2D.padRect(adjRect, blurOffset + 1, blurOffset + 1))
		.stroke();
	if (hasThickerOutline) {
		ctx.lineWidth = lineWidth * 1.5;
		ctx
			.bp()
			.rect(rect)
			.stroke(outlineColor);
	}
	ctx.restore();
};

const drawFlatRect = function(ctx, rect, props = {}) {
	let outlineColor = props.outlineColor;
	if (!outlineColor) {
		if (FLAGS.cleanMode) {
			outlineColor = "#999999";
		} else {
			outlineColor = defaultColors.rectOutline;
		}
	}
	const fillColor = props.fillColor || defaultColors.defaultFill;
	let lineWidth = CanvasUtil.getOptimalLineWidth(ctx);
	if (props.hasThickerOutline) {
		lineWidth *= 1.5;
	}
	if (props.outlineWidthMultiplier) {
		lineWidth *= props.outlineWidthMultiplier;
	}
	ctx.save();
	ctx.lineWidth = lineWidth;
	ctx
		.bp()
		.rect(rect)
		.fill(fillColor)
		.stroke(outlineColor);
	ctx.restore();
};

const drawStrokeRect = function(ctx, rect, props = {}) {
	const outlineColor = props.outlineColor || defaultColors.strokeRectOutline;
	let lineWidth = CanvasUtil.getOptimalLineWidth(ctx);
	if (props.hasThickerOutline) {
		lineWidth *= 2;
	}
	ctx.save();
	ctx.lineWidth = lineWidth;
	ctx.strokeStyle = outlineColor;
	ctx.bp().strokeRect(...rect);
	ctx.restore();
};

// Helper, a hack to fix T12921. Call after clipping to fill in the current
// path with white (or the optional fillStyle argument). When Windows Firefox
// fixes their bug, we can remove this.
const whiteOutAfterClip = function(ctx, fillStyle) {
	const oldFill = ctx.fillStyle;
	ctx.fillStyle = fillStyle || "#FFF";
	ctx.fill();
	ctx.fillStyle = oldFill;
};

const generateBevelPoints = function(rect, options) {
	const {cutAngle, bevelWidth} = options;

	const rectLeft = Math.floor(rect.x);
	const rectRight = Math.ceil(rect.x + rect.w);
	const rectTop = Math.floor(rect.y);
	const rectBottom = Math.ceil(rect.y + rect.h);

	/**
	 *  a = cutAngle
	 *  w = bevelWidth
	 *   *
	 *   * *
	 *   *  * h
	 *   * a *
	 * l *    *
	 *   * * * *
	 *      w
	 */
	const h = bevelWidth / Math.sin(cutAngle);
	const l = h * Math.cos(cutAngle);

	// Compute the 4 needed inset points
	const leftTop = Math.ceil(rectLeft + bevelWidth);
	const topL = Math.ceil(rectTop + l);
	const rightBtm = Math.floor(rectRight - bevelWidth);
	const btmL = Math.floor(rectBottom - l);

	const insetLeftTop = [leftTop, topL];
	const insetLeftBottom = [leftTop, btmL];
	const insetRightTop = [rightBtm, topL];
	const insetRightBottom = [rightBtm, btmL];

	// And the 4 outer rect points
	const rectTopLeft = [rectLeft, rectTop];
	const rectBottomLeft = [rectLeft, rectBottom];
	const rectBottomRight = [rectRight, rectBottom];
	const rectTopRight = [rectRight, rectTop];

	const leftBevelPoints = [
		rectTopLeft,
		rectBottomLeft,
		insetLeftBottom,
		insetLeftTop,
		rectTopLeft,
	];

	const rightBevelPoints = [
		rectTopRight,
		rectBottomRight,
		insetRightBottom,
		insetRightTop,
		rectTopRight,
	];

	const topBevelPoints = [
		rectTopLeft,
		rectTopRight,
		insetRightTop,
		insetLeftTop,
		rectTopLeft,
	];

	const bottomBevelPoints = [
		rectBottomLeft,
		rectBottomRight,
		insetRightBottom,
		insetLeftBottom,
		rectBottomLeft,
	];

	return {
		outerBevel: {
			left: leftBevelPoints,
			top: topBevelPoints,
			right: rightBevelPoints,
			bottom: bottomBevelPoints,
		},
	};
};

const drawRectBevelPart = function(ctx, points, fill) {
	ctx.fillStyle = fill;
	const otherPoints = _.drop(points, 1);
	ctx.beginPath();
	ctx.moveTo(...points[0]);
	otherPoints.forEach(pt => {
		ctx.lineTo(...pt);
	});
	ctx.fill();
};

const drawRectBevel = function(ctx, bevel, fillColors) {
	["left", "top", "right", "bottom"].forEach(dir => {
		const pts = bevel[dir];
		const fill = fillColors[dir];
		drawRectBevelPart(ctx, pts, fill);
	});
};

const drawBevelledRect = function(ctx, rect, drawProps, {options, cache} = {}) {
	// These don't ever move, so we cache these so we don't have
	// to compute them over and over again
	cache._bevelRectPoints =
		cache._bevelRectPoints || generateBevelPoints(rect, options);

	const {outerBevel} = cache._bevelRectPoints;
	drawRectBevel(ctx, outerBevel, options.fillColors);
};

module.exports = {
	createRoundedClippingRegion,
	drawInsetRect,
	drawOutsetRect,
	setContextOutsetShadow,
	drawRoundedRect,
	drawFlatRect,
	drawStrokeRect,
	whiteOutAfterClip,
	drawBevelledRect,
};
